/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/// <reference types="cache-fm" />
import EventTags from 'apmui/EventTags';
import App from 'apmui/App';
import PageType from 'apmui/page/PageType';
import { IResourceStore } from 'apmui/resource';
import { ResourceLoader, RESOURCE_LIST_URL, IResourceId, IResourceMap } from 'apmui/resource/ResourceLoader';
import {
    putResources,
    assignToSections,
    removeFromSections,
    assignAtPosition,
    createOrUpdateFolders,
} from 'apmui/resource/ResourceStore';
import { RESOURCE } from 'apmui/resource/ResourceType';
import { webtopSessionTimeout } from './SessionTimeout';
import { setCookie } from 'apmui/common/Cookie';
import h from 'apmui/util/createElement';

import './VdiDialogController';
import 'apmui/resource/Launch';
import { createDefaultSections, DEFAULT_SECTION_ID } from 'apmui/resource/webtopSection/createDefaultSections';

const app = App.get();

const CACHE_FM = '/private/fm/cache-fm.js';
const LOGOUT_URL = '/my.logout.php3';

function getHost(): string {
    // For IPv6 addresses IE returns the host with no brackets around.
    // Add the brackets around for IPv6 address
    const host = window.location.host;
    if (host.split(':').length > 2 && host.charAt(0) !== '[') {
        return '[' + host + ']';
    }
    return host;
}

app.subscribeDefault(EventTags.START, (event, store) => {
    if (store.pageType !== PageType.WEBTOP) {
        return;
    }
    // TODO: Promisify?
    const body = document.getElementsByTagName('body')[0];
    if (body) {
        window.ur_firepass_host = window.location.protocol + '//' + getHost();
        /* ur_baseurl is set to http://127.0.0.1:8888, just in case. no relative urls yet.. */
        window.ur_baseurl = '/f5-w-687474703a2f2f3132372e302e302e313a38383838$$/';
        window.F5_ST_adm_no_autostart = 1;
        window.F5_ST_adm_enabled = 1;
        window.F5_HT_adm_enabled = 1;
        body.appendChild(
            h('script', {
                src: CACHE_FM,
                onload: () => webtopSessionTimeout.start(),
            })
        );
    }
    // TODO: rework callbacks to call events instead of this
    const resourceLoader = new ResourceLoader();
    resourceLoader.onResourceList = (resourceMap: IResourceMap) => {
        const defaultSections = createDefaultSections(resourceMap, {
            [DEFAULT_SECTION_ID.FALLBACK]: store.webtop.config.section.fallbackCaption,
            [DEFAULT_SECTION_ID.LEGACY_NA]: store.webtop.config.section.networkAccessCaption,
            [DEFAULT_SECTION_ID.LEGACY_OTHER]: store.webtop.config.section.applicationLinksCaption,
        });
        if (defaultSections) {
            app.dispatch(EventTags.RESOURCE_INFO_RECEIVED, { resourceMap: defaultSections });
        }
    };
    resourceLoader.onResourceInfo = (resourceMapChunk: IResourceMap) => {
        app.dispatch(EventTags.RESOURCE_INFO_RECEIVED, { resourceMap: resourceMapChunk });
    };
    resourceLoader.onComplete = (resourceMap: IResourceMap) => {
        app.dispatch(EventTags.RESOURCE_LOADING_COMPLETE, { resourceMap });
    };
    resourceLoader.load(RESOURCE_LIST_URL.WEBTOP);
});

/* Session logout */
app.subscribe(
    EventTags.LOGOUT,
    (event, store) => {
        /* ATTENTION: No more actions are possible after this function. */
        if (top.F5_HT_logOut) {
            top.F5_HT_logOut();
        } else {
            window.location.href = LOGOUT_URL;
        }
    },
    { default: true, last: true }
);

app.subscribe(EventTags.RESOURCE_INFO_RECEIVED, ({ detail }, store) => {
    if (store.pageType !== PageType.WEBTOP) {
        return;
    }
    if (!store[store.pageType].resources) {
        store[store.pageType].resources = {};
    }
    const resources = store[store.pageType].resources as IResourceStore;
    const resourceMap = 'resourceMap' in detail ? detail.resourceMap : {};

    switch (detail.method) {
        case 'assign':
            assignToSections(resources, detail.resourceIDs, detail.sections);
            return;
        case 'remove':
            removeFromSections(resources, detail.resourceIDs, detail.sections);
            return;
        case 'replace': {
            /**
             * This is replace method code, it used for replacing one of the VDI servers
             * with resources returned from server and putting these resources into sections
             */
            const resourceIds: IResourceId[] = [];
            const folderIds = createOrUpdateFolders(resources, resourceMap as IResourceMap);
            /* add all new folders in front of new resources */
            resourceIds.push(...folderIds);

            for (const type in resourceMap) {
                if (resourceMap.hasOwnProperty(type) && type !== RESOURCE.WEBTOP_FOLDER) {
                    for (const id in resourceMap[type].resources) {
                        if (resourceMap[type].resources.hasOwnProperty(id)) {
                            resourceIds.push({ type: type as RESOURCE, id });
                        }
                    }
                }
            }

            const replace = detail.replace;
            assignAtPosition(resources, resourceIds, replace);
            removeFromSections(resources, [replace]);
        }
    }

    const newResources = putResources(resources, resourceMap);
    app.dispatch(EventTags.RESOURCE_STORE_CHANGED, newResources);
});

app.subscribe(EventTags.RESOURCE_LOADING_COMPLETE, (_, store) => {
    if (store.pageType !== PageType.WEBTOP) {
        return;
    }
    const pagetype = store[store.pageType];
    if (!pagetype.resources || !pagetype.resources[RESOURCE.PORTAL_ACCESS]) {
        // no PA resources: suppress URL input
        pagetype.config.urlInput.enabled = '0';
    }
    // set Full Webtop cookie to disable Home Tab
    setCookie('F5_fullWT', '1', '/');
    // For one who depends on it
    app.dispatch(EventTags.WEBTOP_CONFIGURATION_COMPLETE);
});
